/**
 * **再生中の描画の速さ**（fps の表示。純関数）。
 *
 * 「カクついて見えるのは描画が遅れているのか、そういう演出なのか」を見分けるためのもの。
 * 舞台を描き直すたびに `{ t: 実時間 ms, ms: 再生位置 }` を 1 つ積み、直近 `windowMs` ぶんで数える:
 *
 * - `fps`     … 実際に描き直した回数 / 秒
 * - `skipped` … 飛ばしたコマの数（再生位置が 1 コマより大きく進んだぶん。素材の fps で数える）
 * - `worstMs` … いちばん長く描き直さなかった間（実時間）
 * - `smooth`  … 素材の fps の 9 割以上で描けていて、コマを飛ばしていない
 *               ＝ それでもカクついて見えるなら**演出そのものの動き**
 */
export const FPS_WINDOW_MS = 1000;

/** 古いものを捨てて新しい 1 つを足す（同じ再生位置は数えない＝止まっている間は増えない） */
export function pushSample(samples, sample, windowMs = FPS_WINDOW_MS) {
  const last = samples[samples.length - 1];
  if (last && Math.abs(last.ms - sample.ms) < 0.5) return samples;
  const out = samples.filter((s) => sample.t - s.t <= windowMs * 2);
  out.push(sample);
  return out;
}

/**
 * @param samples `[{ t, ms }]`（古い順）
 * @param now 今の実時間
 * @param fps 素材の fps（コマ飛びの基準）
 */
export function fpsStats(samples = [], now = 0, fps = 30, windowMs = FPS_WINDOW_MS) {
  const recent = samples.filter((s) => now - s.t <= windowMs);
  if (recent.length < 2) return { fps: 0, skipped: 0, worstMs: 0, smooth: false, count: recent.length };
  const frameMs = 1000 / (fps > 0 ? fps : 30);
  let skipped = 0;
  let worstMs = 0;
  for (let i = 1; i < recent.length; i += 1) {
    const dMs = recent[i].ms - recent[i - 1].ms;
    const dT = recent[i].t - recent[i - 1].t;
    if (dMs > 0) skipped += Math.max(0, Math.round(dMs / frameMs) - 1);
    worstMs = Math.max(worstMs, dT);
  }
  const span = recent[recent.length - 1].t - recent[0].t;
  const measured = span > 0 ? ((recent.length - 1) * 1000) / span : 0;
  const rounded = Math.round(measured * 10) / 10;
  return {
    fps: rounded,
    skipped,
    worstMs: Math.round(worstMs),
    smooth: rounded >= (fps > 0 ? fps : 30) * 0.9 && skipped === 0,
    count: recent.length,
  };
}
